'use client';

import { useEffect, useState } from "react";
import { loadA11yFlyoutContent } from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/data-access/preload";
import { ComponentA11Ya11YFeature, SlideOutA11Y } from "@/app/lib/generated/graphql-types";
import { notNull } from "@/app/lib/common/not-null";
import Grid from "@mui/material/Grid2";
import { Stack } from "@mui/system";
import { Box, Button, SvgIconProps, Typography } from "@mui/material";
import { Accessible, BabyChangingStation, DirectionsCar, Elevator, Wc } from "@mui/icons-material";
import FlyoutWrapper from "@/app/features/sites/header/ui/desktop/flyout-info/FlyoutWrapper";
import Link from "next/link";
import ErrorBox from "@/app/__to_be_integrated/_____components/_NEW/ErrorBox";

export default function A11yFlyoutInfo() {
    const [content, setContent] = useState<SlideOutA11Y>();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            const { data, error } = await loadA11yFlyoutContent();
            if (error || !data) {
                console.error('Error loading a11y flyout content:', error);
                setError('Fehler beim Laden der Inhalte. Bitte versuchen Sie es später erneut.');
                return;
            }

            setContent(data);
        })()
    }, []);

    if (error) return <ErrorBox error={error} />
    if (content === undefined) return null;

    const features = content.features.filter(notNull);

    return (
        <FlyoutWrapper backgroundImage={content.image} fit="full">
            <Grid
                container
                spacing={{ xs: 4, md: 8 }}
                columns={{ xs: 1, md: features.length }}
            >
                {features.map((feature, index) =>
                    <Grid key={index} size={{ xs: 1, md: 1 }}>
                        <FeatureTile feature={feature} />
                    </Grid>
                )}
            </Grid>
            <Box className="flex flex-col justify-center items-center mt-8 md:mt-16">
                <Typography variant="h6" component="p" color="primary">Barrierefreiheiter Webauftritt</Typography>
                <Typography variant="body1" className="text-center">Wir arbeiten stetig an der Verbesserung der Barrierefreiheit unserer Webseite und setzen uns dafür ein, alle Inhalte für alle Benutzer zugänglich zu machen. </Typography>
                <Button
                    variant="outlined"
                    component={Link}
                    href="/a11y-statement"
                    sx={{ my: 2, textTransform: 'uppercase', textAlign: 'center' }}
                    prefetch
                >
                    Zur Barrierefreiheitserklärung
                </Button>
            </Box>
        </FlyoutWrapper>
    )
}

function FeatureTile({ feature }: { feature: ComponentA11Ya11YFeature }) {
    return (
        <Stack spacing={4} alignItems="center">
            <IconFromName name={feature.iconName as IconName} sx={{ fontSize: { xs: '3rem', md: '5rem' }, color: 'primary' }}/>
            <Stack spacing={2}>
                <Typography
                    variant="h4"
                    color="primary"
                    textAlign="center"
                >
                    {feature.title}
                </Typography>
                <Typography
                    variant="body1"
                    className="text-center"
                    textAlign="center"
                >
                    {feature.description}
                </Typography>
            </Stack>
        </Stack>
    )
}

type IconName = keyof typeof icons;

const icons = {
    DirectionsCar,
    Elevator,
    BabyChangingStation,
    Accessible,
    Wc
};

function IconFromName({ name, ...props }: { name: IconName } & SvgIconProps) {
    const Icon = icons[name];
    return <Icon {...props} />;
}
